import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import AssetNav from "../components/AssetNav";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import FileUpload from "../components/FileUpload";
import { toast } from "sonner";

const STATUSES = ["open", "assigned", "in_progress", "waiting_parts", "completed", "closed", "cancelled"];
const S_COLORS = { open: "bg-red-100 text-red-800", assigned: "bg-amber-100 text-amber-800", in_progress: "bg-blue-100 text-blue-800", waiting_parts: "bg-purple-100 text-purple-800", completed: "bg-emerald-100 text-emerald-800", closed: "bg-slate-100 text-slate-600", cancelled: "bg-slate-100 text-slate-500" };
const PRIORITIES = ["low", "medium", "high", "urgent"];
const P_COLORS = { low: "text-slate-500", medium: "text-slate-700", high: "text-orange-600", urgent: "text-red-600 font-semibold" };

const EMPTY = { unit_id: "", item_id: "", department_id: "", room_id: "", description: "", priority: "medium", technician: "", vendor_id: "", estimated_cost: "" };

export default function AssetMaintenance() {
  const [jobs, setJobs] = useState([]);
  const [units, setUnits] = useState([]);
  const [items, setItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [depts, setDepts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [statusJob, setStatusJob] = useState(null);          // job pending status change
  const [statusForm, setStatusForm] = useState({ status: "", cost: "", resolution: "", note: "" });
  const [detail, setDetail] = useState(null);                // job open in detail dialog

  const load = async () => {
    const [j, u, it, r, d, v] = await Promise.all([
      api.get("/assets/maintenance"), api.get("/assets/units"), api.get("/assets/items"),
      api.get("/rooms"), api.get("/assets/departments"), api.get("/assets/vendors"),
    ]);
    setJobs(j.data); setUnits(u.data.filter(x => x.status !== "disposed")); setItems(it.data);
    setRooms(r.data); setDepts(d.data.filter(x => x.active)); setVendors(v.data);
  };
  useEffect(() => { load(); }, []);

  const deptName = id => depts.find(d => d.id === id)?.name || (id ? "—" : "");
  const itemName = id => items.find(i => i.id === id)?.name || "";
  const roomNum = id => rooms.find(r => r.id === id)?.number || "";
  const vendorName = id => vendors.find(v => v.id === id)?.name || "";

  const create = async () => {
    if (!f.description || f.description.trim().length < 3) return toast.error("Description (min 3 chars) required");
    try {
      await api.post("/assets/maintenance", {
        unit_id: f.unit_id || null, item_id: f.item_id || null, department_id: f.department_id || null,
        room_id: f.room_id || null, description: f.description, priority: f.priority,
        technician: f.technician || null, vendor_id: f.vendor_id || null,
        estimated_cost: f.estimated_cost === "" ? null : Number(f.estimated_cost),
      });
      toast.success("Complaint logged"); setF(EMPTY); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openStatus = (job, status) => {
    setStatusJob(job);
    setStatusForm({ status, cost: job.cost ?? "", resolution: job.resolution ?? "", note: "" });
  };
  const submitStatus = async () => {
    try {
      const patch = { status: statusForm.status, note: statusForm.note || null };
      if (["completed", "closed"].includes(statusForm.status)) {
        if (statusForm.cost !== "") patch.cost = Number(statusForm.cost);
        if (statusForm.resolution) patch.resolution = statusForm.resolution;
      }
      await api.patch(`/assets/maintenance/${statusJob.id}`, patch);
      toast.success(`Status → ${statusForm.status.replace("_", " ")}`); setStatusJob(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const needsExtra = ["completed", "closed"].includes(statusForm.status);

  const addAttachment = async (job, url) => {
    if (!url) return;
    try {
      const { data } = await api.patch(`/assets/maintenance/${job.id}`, { attachment: url });
      toast.success("Attachment added"); setDetail(data); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Layout>
      <div className="mb-4"><h1 className="section-title">Maintenance Register</h1></div>
      <AssetNav />

      <Card className="p-4 mb-4" data-testid="maintenance-form">
        <div className="text-sm font-semibold text-slate-700 mb-3">Log a complaint</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <div><Label>Tagged Asset</Label><Select value={f.unit_id} onValueChange={v => setF({ ...f, unit_id: v, item_id: "" })}><SelectTrigger className="h-10 mt-1" data-testid="job-unit"><SelectValue placeholder="— optional —" /></SelectTrigger><SelectContent>{units.map(u => <SelectItem key={u.id} value={u.id}>{u.asset_id} · {u.item_name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>or Item (bulk/consumable)</Label><Select value={f.item_id} onValueChange={v => setF({ ...f, item_id: v, unit_id: "" })}><SelectTrigger className="h-10 mt-1" data-testid="job-item"><SelectValue placeholder="— optional —" /></SelectTrigger><SelectContent>{items.filter(i => i.active).map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Department</Label><Select value={f.department_id} onValueChange={v => setF({ ...f, department_id: v })}><SelectTrigger className="h-10 mt-1" data-testid="job-dept"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{depts.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Room / Location</Label><Select value={f.room_id} onValueChange={v => setF({ ...f, room_id: v })}><SelectTrigger className="h-10 mt-1" data-testid="job-room"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{rooms.map(r => <SelectItem key={r.id} value={r.id}>Room {r.number}</SelectItem>)}</SelectContent></Select></div>
          <div className="col-span-2"><Label>Complaint / Description *</Label><Input value={f.description} onChange={e => setF({ ...f, description: e.target.value })} className="h-10 mt-1" data-testid="job-description" /></div>
          <div><Label>Priority</Label><Select value={f.priority} onValueChange={v => setF({ ...f, priority: v })}><SelectTrigger className="h-10 mt-1" data-testid="job-priority"><SelectValue /></SelectTrigger><SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Est. Cost</Label><Input type="number" min="0" value={f.estimated_cost} onChange={e => setF({ ...f, estimated_cost: e.target.value })} className="h-10 mt-1" data-testid="job-est-cost" /></div>
          <div><Label>Technician</Label><Input value={f.technician} onChange={e => setF({ ...f, technician: e.target.value })} className="h-10 mt-1" data-testid="job-technician" /></div>
          <div><Label>Vendor</Label><Select value={f.vendor_id} onValueChange={v => setF({ ...f, vendor_id: v })}><SelectTrigger className="h-10 mt-1" data-testid="job-vendor"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="md:col-span-2 flex items-end"><Button onClick={create} className="h-10 w-full bg-slate-900 text-white hover:bg-slate-800" data-testid="create-job">Log Complaint</Button></div>
        </div>
      </Card>

      <Card className="p-0 overflow-x-auto">
        <table className="vr-table" data-testid="maintenance-table">
          <thead><tr>
            <th>Complaint #</th><th>Date</th><th>Asset / Item</th><th>Dept</th><th>Room</th>
            <th>Priority</th><th>Technician / Vendor</th><th className="num">Est.</th><th className="num">Actual</th>
            <th>Status</th><th>By</th><th className="no-print">Update</th><th className="no-print"></th>
          </tr></thead>
          <tbody>
            {jobs.map(j => (
              <tr key={j.id}>
                <td className="font-mono text-xs">{j.number}</td>
                <td className="text-xs">{(j.complaint_date || "").slice(0, 10)}</td>
                <td className="text-xs">{j.asset_id || itemName(j.item_id) || "—"}</td>
                <td className="text-xs">{deptName(j.department_id) || "—"}</td>
                <td className="text-xs">{j.room_id ? `Room ${roomNum(j.room_id)}` : "—"}</td>
                <td className={`text-xs capitalize ${P_COLORS[j.priority] || ""}`}>{j.priority}</td>
                <td className="text-xs">{j.technician || vendorName(j.vendor_id) || "—"}</td>
                <td className="num text-xs">{j.estimated_cost ?? "—"}</td>
                <td className="num text-xs">{j.cost ?? "—"}</td>
                <td><Badge className={`border ${S_COLORS[j.status] || ""}`}>{j.status.replace("_", " ")}</Badge></td>
                <td className="text-xs">{j.created_by_name}</td>
                <td className="no-print">
                  {!["closed", "cancelled"].includes(j.status) && (
                    <Select value="" onValueChange={v => openStatus(j, v)}>
                      <SelectTrigger className="h-8 w-36 text-xs" data-testid={`job-status-${j.number}`}><SelectValue placeholder="Change status" /></SelectTrigger>
                      <SelectContent>{STATUSES.filter(s => s !== j.status).map(s => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </td>
                <td className="no-print"><Button size="sm" variant="ghost" onClick={() => setDetail(j)} data-testid={`job-detail-${j.number}`}>Details</Button></td>
              </tr>
            ))}
            {jobs.length === 0 && <tr><td colSpan={13} className="text-center text-slate-500 py-6">No complaints logged.</td></tr>}
          </tbody>
        </table>
      </Card>

      {/* Status change dialog */}
      <Dialog open={!!statusJob} onOpenChange={o => !o && setStatusJob(null)}>
        <DialogContent data-testid="status-dialog">
          <DialogHeader><DialogTitle>{statusJob?.number} → {statusForm.status.replace("_", " ")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {needsExtra && (
              <>
                <div><Label>Actual cost</Label><Input type="number" min="0" value={statusForm.cost} onChange={e => setStatusForm({ ...statusForm, cost: e.target.value })} className="h-10 mt-1" data-testid="status-cost" /></div>
                <div><Label>Resolution notes</Label><Input value={statusForm.resolution} onChange={e => setStatusForm({ ...statusForm, resolution: e.target.value })} className="h-10 mt-1" data-testid="status-resolution" /></div>
              </>
            )}
            <div><Label>Note (added to history)</Label><Input value={statusForm.note} onChange={e => setStatusForm({ ...statusForm, note: e.target.value })} className="h-10 mt-1" data-testid="status-note" /></div>
            {["closed", "cancelled"].includes(statusForm.status) && <div className="text-xs text-amber-700">Closing/cancelling is final and requires Supervisor/Admin rights.</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusJob(null)}>Cancel</Button>
            <Button onClick={submitStatus} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="status-submit">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / history dialog */}
      <Dialog open={!!detail} onOpenChange={o => !o && setDetail(null)}>
        <DialogContent data-testid="detail-dialog">
          <DialogHeader><DialogTitle>{detail?.number} — {detail?.description}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-slate-500">Asset/Item:</span> {detail.asset_id || itemName(detail.item_id) || "—"}</div>
                <div><span className="text-slate-500">Department:</span> {deptName(detail.department_id) || "—"}</div>
                <div><span className="text-slate-500">Priority:</span> <span className="capitalize">{detail.priority}</span></div>
                <div><span className="text-slate-500">Room:</span> {detail.room_id ? `Room ${roomNum(detail.room_id)}` : "—"}</div>
                <div><span className="text-slate-500">Technician:</span> {detail.technician || "—"}</div>
                <div><span className="text-slate-500">Vendor:</span> {vendorName(detail.vendor_id) || "—"}</div>
                <div><span className="text-slate-500">Est. cost:</span> {detail.estimated_cost ?? "—"}</div>
                <div><span className="text-slate-500">Actual cost:</span> {detail.cost ?? "—"}</div>
                <div><span className="text-slate-500">Completed:</span> {(detail.completed_at || "—").slice(0, 16).replace("T", " ")}</div>
                <div><span className="text-slate-500">Closed by:</span> {detail.closed_by_name || "—"}</div>
              </div>
              {detail.resolution && <div><span className="text-slate-500">Resolution:</span> {detail.resolution}</div>}
              <div className="pt-2 border-t border-slate-100">
                <div className="text-xs font-semibold text-slate-600 mb-1">Attachments (fault / repair photos)</div>
                <div className="flex flex-wrap gap-3 mb-2">
                  {(detail.attachments || []).map((a, i) => (
                    <a key={a} href={`${process.env.REACT_APP_BACKEND_URL}${a}`} target="_blank" rel="noreferrer" className="text-xs text-blue-700 underline" data-testid={`maint-attach-view-${i}`}>Attachment {i + 1}</a>
                  ))}
                  {(!detail.attachments || detail.attachments.length === 0) && <span className="text-xs text-slate-400">None yet</span>}
                </div>
                {!["closed", "cancelled"].includes(detail.status) && (
                  <FileUpload value={null} onChange={url => addAttachment(detail, url)} testid="maint-attach" />
                )}
              </div>
              <div className="pt-2 border-t border-slate-100">
                <div className="text-xs font-semibold text-slate-600 mb-1">History</div>
                <div className="space-y-1 max-h-52 overflow-y-auto">
                  {(detail.history || []).map((h, idx) => (
                    <div key={`${h.at}-${idx}`} className="text-xs text-slate-600">
                      <span className="font-mono">{(h.at || "").slice(0, 16).replace("T", " ")}</span> · <span className="capitalize">{(h.status || "").replace("_", " ")}</span> · {h.by}{h.note ? ` — ${h.note}` : ""}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
